"use client";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ResponsiveContainer,
  CartesianGrid,
  YAxis,
  Tooltip,
  Bar,
  XAxis,
  BarChart,
} from "recharts";
import { StatisticMonthlyBreakdown } from "modules/invoice/types/invoice.interface";

const COLORS = {
  paid: "#10b981",
  onHold: "#6b7280",
  late: "#ef4444",
};

const formatCurrency = (value: number) => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
  return value.toString();
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload) return null;
  return (
    <div className="rounded-lg border border-gray-100 bg-white px-4 py-3 shadow-lg">
      <p className="mb-2 text-sm font-semibold text-gray-900">{label}</p>
      {payload.map((entry: any, i: number) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-gray-500">{entry.name}</span>
          <span className="ml-auto font-medium text-gray-900">
            {entry.value.toLocaleString("fr-FR")} &euro;
          </span>
        </div>
      ))}
    </div>
  );
};

export const RevenueEvolutionChart = ({
  data,
}: {
  data: StatisticMonthlyBreakdown[];
}) => {
  const chartData = data.map((item) => {
    const monthName = new Date(2000, item.month - 1).toLocaleString("fr-FR", {
      month: "short",
    });
    return {
      name: monthName.charAt(0).toUpperCase() + monthName.slice(1),
      paid: item.paid,
      onHold: item.onHold,
      late: item.late,
    };
  });

  return (
    <Card className="col-span-full lg:col-span-2">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold text-gray-900">
          Revenus mensuels
        </CardTitle>
        <div className="flex gap-4 pt-1">
          {[
            { label: "Encaissé", color: COLORS.paid },
            { label: "En attente", color: COLORS.onHold },
            { label: "En retard", color: COLORS.late },
          ].map(({ label, color }) => (
            <div key={label} className="flex items-center gap-1.5">
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: color }}
              />
              <span className="text-xs text-gray-500">{label}</span>
            </div>
          ))}
        </div>
      </CardHeader>
      <CardContent className="h-[350px] pt-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 5, right: 10, left: -10, bottom: 5 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#f3f4f6"
            />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: "#9ca3af" }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#9ca3af" }}
              tickLine={false}
              axisLine={false}
              tickFormatter={formatCurrency}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f9fafb" }} />
            <Bar
              dataKey="paid"
              name="Encaissé"
              fill={COLORS.paid}
              radius={[4, 4, 0, 0]}
              maxBarSize={32}
            />
            <Bar
              dataKey="onHold"
              name="En attente"
              fill={COLORS.onHold}
              radius={[4, 4, 0, 0]}
              maxBarSize={32}
            />
            <Bar
              dataKey="late"
              name="En retard"
              fill={COLORS.late}
              radius={[4, 4, 0, 0]}
              maxBarSize={32}
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};
